import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from "shared/utils/firebase";
import { signOut } from 'firebase/auth';
import { Sun, Moon, ArrowLeft } from 'react-feather';
import styles from './CodeList.module.css';
import UserProfileDropdown from 'shared/components/Auth/UserProfileDropdown';
    
const CodeList = ({ userDetails, auth: initialAuth, theme, toggleTheme }) => {
    const navigate = useNavigate();
    const [testCounts, setTestCounts] = useState({});
    const [userProgress, setUserProgress] = useState({});
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);

    // Redirect to login if user logs out
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Base language configurations (icons and colors only)
    const languageConfig = {
        'Verilog': { icon: <img src="/code_icons/verilog_icon.svg" alt="Verilog" className={styles.iconImage} />, color: '#081665ff' },
        'SystemVerilog': { icon: <img src="/code_icons/systemverilog_icon.svg" alt="SystemVerilog" className={styles.iconImage} />, color: '#7E57C2' },
        'VHDL': { icon: <img src="/code_icons/vhdl_icon.png" alt="Vhdl" className={styles.iconImage} />, color: '#57c29eff' },
        'Matlab': { icon: <img src="/code_icons/Matlab_icon.png" alt="Matlab" className={styles.iconImage} />, color: '#EF5350' },
        'Assembly': { icon: <img src="/code_icons/assembly_icon.png" alt="Assembly" className={styles.iconImage} />, color: '#8D6E63' },
        'C': { icon: <img src="/code_icons/C_icon.png" alt="C" className={styles.iconImage} />, color: '#2679a6ff' },
        'C++': { icon: <img src="/code_icons/C++_icon.png" alt="C++" className={styles.iconImage} />, color: '#1976D2' },
        'Python': { icon: <img src="/code_icons/python_icon.png" alt="Python" className={styles.iconImage} />, color: '#FFA726' },
        'UVM': { icon: <img src="/code_icons/systemverilog_icon.svg" alt="UVM" className={styles.iconImage} />, color: '#E91E63' }
    };

    const difficulties = ['Easy', 'Medium', 'Advanced'];

    // Difficulty level descriptions and colors
    const difficultyConfig = {
        'Easy': {
            label: 'Beginner',
            color: '#4CAF50',
            emoji: '🌱',
            description: 'Perfect for beginners. Start with fundamental concepts and basic syntax.'
        },
        'Medium': {
            label: 'Intermediate',
            color: '#FF9800',
            emoji: '🍊',
            description: 'Build on basics. Tackle moderate challenges and problem-solving.'
        },
        'Advanced': {
            label: 'Advanced',
            color: '#F44336',
            emoji: '🏆',
            description: 'Master level problems. Complex algorithms and optimization.'
        }
    };

    // Fetch test counts from database
    useEffect(() => {
        const fetchTestCounts = async () => {
            try {
                setLoading(true);
                const counts = {};

                const q = query(
                    collection(db, 'codeExercises'),
                    where('isPublished', '==', true)
                );

                const snapshot = await getDocs(q);

                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    const category = data.category || '';
                    const difficulty = data.difficulty || '';
                    const key = `${category}-${difficulty}`;
                    counts[key] = (counts[key] || 0) + 1;
                });

                setTestCounts(counts);
            } catch (error) {
                console.error('Error fetching test counts:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTestCounts();
    }, []);

    // Fetch user progress
    useEffect(() => {
        const fetchUserProgress = async () => {
            if (!auth.currentUser) return;

            try {
                const q = query(
                    collection(db, 'codeProgress'),
                    where('userId', '==', auth.currentUser.uid)
                );

                const snapshot = await getDocs(q);
                const progress = {};

                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.language && data.difficulty) {
                        const langKey = Object.keys(languageConfig).find(k => k.toLowerCase() === data.language.toLowerCase()) || data.language;
                        const diffKey = difficulties.find(d => d.toLowerCase() === data.difficulty.toLowerCase()) || data.difficulty;

                        const key = `${langKey}-${diffKey}`;
                        progress[key] = (progress[key] || 0) + 1;
                    }
                });

                setUserProgress(progress);
            } catch (error) {
                console.error('Error fetching user progress:', error);
            }
        };

        fetchUserProgress();
    }, [isAuthenticated]);

    // Generate all combinations of languages and difficulties
    const generateCards = () => {
        const cards = [];

        Object.keys(languageConfig).forEach(language => {
            difficulties.forEach(difficulty => {
                const key = `${language}-${difficulty}`;
                const count = testCounts[key] || 0;
                const config = languageConfig[language];
                const diffConfig = difficultyConfig[difficulty];

                const completedCount = userProgress[key] || 0;
                const percentage = count > 0 ? Math.round((completedCount / count) * 100) : 0;

                if (count > 0) {
                    cards.push({
                        id: `${language.toLowerCase()}-${difficulty.toLowerCase()}`,
                        language: language,
                        difficulty: difficulty,
                        name: `${language} - ${difficulty}`,
                        icon: config.icon,
                        color: config.color,
                        difficultyLabel: diffConfig.label,
                        difficultyColor: diffConfig.color,
                        difficultyEmoji: diffConfig.emoji,
                        description: diffConfig.description,
                        testCount: count,
                        completedCount: completedCount,
                        percentage: percentage
                    });
                }
            });
        });

        return cards;
    };

    const codingCards = generateCards();

    const handleLanguageClick = (languageId) => {
        navigate(`/exercise/${languageId}`);
    };

    return (
        <div className={`${styles.codeListContainer} ${theme === 'light' ? styles.lightTheme : ''}`}>
            {/* Brand Header */}
            <header className={styles.headerBar}>
                <div className={styles.logoContainer} onClick={() => navigate('/')} title="Back to home">
                    <img src="/static_img/weblogo.png" alt="SiliconLogix Logo" className={styles.webLogo} />
                </div>

                <div className={styles.navActions}>
                    <button className={styles.themeToggleBtn} onClick={toggleTheme} title="Switch theme">
                        {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                    </button>
                    {isAuthenticated && (
                        <UserProfileDropdown userDetails={userDetails} styles={styles} />
                    )}
                </div>
            </header>

            <main className={styles.mainContent}>
                {/* Back Navigation */}
                <button className={styles.btnNavBack} onClick={() => navigate('/')}>
                    <ArrowLeft size={13} />
                    <span>Back to Home</span>
                </button>

                <div className={styles.header}>
                    <div className={styles.headerText}>
                        <h1 className={styles.title}>Practice Exercises</h1>
                        <h2 className={styles.subtitle}>Choose a hardware description or programming language to solve practice problems</h2>
                    </div>
                </div>

                {!loading && codingCards.length === 0 ? (
                    <div className={styles.emptyState}>
                        <h3 className={styles.emptyTitle}>No Practice Exercises Available</h3>
                        <p className={styles.emptyDesc}>There are currently no published questions. Please check back later!</p>
                    </div>
                ) : (
                    <div className={styles.languageGrid}>
                        {codingCards.map((card) => (
                            <div
                                key={card.id}
                                className={styles.languageCard}
                                onClick={() => handleLanguageClick(card.id)}
                                style={{ '--card-color': card.color }}
                            >
                                <div className={styles.cardHeader}
                                    style={{ backgroundColor: card.color }}
                                >
                                    {/* Circular Progress */}
                                    <div className={styles.circularProgress}>
                                        <svg className={styles.progressRing} width="48" height="48">
                                            <circle
                                                className={styles.progressRingCircle}
                                                stroke="rgba(0,0,0,0.15)"
                                                strokeWidth="4"
                                                fill="transparent"
                                                r="18"
                                                cx="24"
                                                cy="24"
                                            />
                                            <circle
                                                className={styles.progressRingCircle}
                                                stroke={card.percentage === 100 ? "#10b981" : "#8b5cf6"}
                                                strokeWidth="4"
                                                fill="transparent"
                                                r="18"
                                                cx="24"
                                                cy="24"
                                                strokeDasharray={`${2 * Math.PI * 18}`}
                                                strokeDashoffset={`${2 * Math.PI * 18 * (1 - card.percentage / 100)}`}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <div className={styles.progressLabel}>
                                            {card.percentage === 100 ? (
                                                <span className={styles.progressIcon}>✓</span>
                                            ) : (
                                                <span className={styles.progressPercentage}>{card.percentage}%</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className={styles.iconWrapper} style={{ color: card.color }}>
                                        <span className={styles.languageIcon}>{card.icon}</span>
                                    </div>
                                </div>

                                <div className={styles.cardBody}>
                                    <h3 className={styles.languageName}>{card.language} {card.difficulty}</h3>
                                    <p className={styles.cardDescription}>{card.description}</p>

                                    <div className={styles.cardFooter}>
                                        <div className={styles.metaInfo}>
                                            <span className={styles.problemCount}>
                                                {loading ? '...' : `${card.completedCount}/${card.testCount}`} {card.testCount === 1 ? 'Question' : 'Questions'}
                                            </span>
                                            <p className={styles.difficultyText} style={{ color: card.difficultyColor }}>{card.difficultyEmoji} {card.difficulty} Level</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default CodeList;
